import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Field, Select, TextArea, TextInput } from '@/components/Field'
import { Kicker } from '@/components/Kicker'
import { Chip } from '@/components/Chip'
import { PhotoAttachInput } from '@/components/PhotoAttachInput'
import { useAppStore } from '@/stores/appStore'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import { PACK_COLORS } from '@/design-system/tokens'
import { toLocalInputValue } from '@/lib/format'
import { emitEvent } from '@/lib/emitEvent'
import { uploadAttachment } from '@/lib/attachments'
import { correctionEventType, payloadValue, useEditableEvent } from '@/lib/editEvent'

type DiaperType = 'wet' | 'dirty' | 'both' | 'dry'

const TYPES: { key: DiaperType; label: string }[] = [
  { key: 'wet', label: 'Wet' },
  { key: 'dirty', label: 'Dirty' },
  { key: 'both', label: 'Both' },
  { key: 'dry', label: 'Dry' },
]

export function DiaperLoggerScreen() {
  const nav = useNavigate()
  const user = useAppStore((s) => s.user)
  const familyId = useAppStore((s) => s.familyId)
  const person = useFocusedPerson()
  const edit = useEditableEvent(['early_care.diaper.recorded', 'early_care.diaper.corrected'])

  const [type, setType] = useState<DiaperType>('wet')
  const [consistency, setConsistency] = useState<'liquid' | 'soft' | 'formed' | 'hard' | ''>('')
  const [color, setColor] = useState<string>('')
  const [occurredAt, setOccurredAt] = useState<string>(toLocalInputValue(new Date()))
  const [note, setNote] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [prefilled, setPrefilled] = useState(false)

  useEffect(() => {
    if (!edit.original || prefilled) return
    const p = edit.original.payload
    const dt = payloadValue<DiaperType>(p, 'diaper_type')
    if (dt) setType(dt)
    const c = payloadValue<typeof consistency>(p, 'consistency')
    if (c) setConsistency(c)
    const col = payloadValue<string>(p, 'color')
    if (col) setColor(col)
    const n = payloadValue<string>(p, 'note')
    if (n) setNote(n)
    setOccurredAt(toLocalInputValue(new Date(edit.original.occurred_at)))
    setPrefilled(true)
  }, [edit.original, prefilled])

  if (!person || !familyId || !user) return <Screen title="Log diaper" back />

  const packColor = PACK_COLORS.early_care.light
  const showBM = type === 'dirty' || type === 'both'

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      if (!person || !familyId || !user) return
      const payload: Record<string, unknown> = { diaper_type: type }
      if (showBM) {
        if (consistency) payload.consistency = consistency
        if (color) payload.color = color
      }
      if (note.trim()) payload.note = note.trim()

      const eventType = edit.original
        ? (correctionEventType(edit.original.event_type) as 'early_care.diaper.corrected')
        : 'early_care.diaper.recorded'

      if (photos.length > 0) payload.has_attachment = true

      const eventId = await emitEvent({
        familyId,
        personId: person.id,
        userId: user.id,
        eventType,
        payload: payload as import('@/lib/types').JSONValue,
        occurredAt: new Date(occurredAt),
        supersedes: edit.supersedesId,
      })
      for (const file of photos) {
        await uploadAttachment({ familyId, userId: user.id, file, eventId })
      }
      if (edit.original) nav('/timeline'); else nav(-1)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const isEditing = !!edit.original
  return (
    <Screen title={isEditing ? 'Correct diaper' : 'Log diaper'} back>
      <form onSubmit={save} className="px-5 py-5 space-y-6">
        {edit.loading && <div className="text-caption text-text-tertiary">Loading original entry…</div>}
        {edit.error && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{edit.error}</div>
        )}
        {isEditing && (
          <div className="rounded-r-sm bg-bg-elevated border border-divider-subtle p-3 text-caption text-text-secondary">
            Correcting an earlier diaper entry. Saving creates a new entry that supersedes the original — the original stays in the record as history.
          </div>
        )}
        <div>
          <Kicker>Type</Kicker>
          <div className="mt-3 flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <Chip
                key={t.key}
                color={packColor}
                emphasis={type === t.key ? 'filled' : 'quiet'}
                onClick={() => setType(t.key)}
              >
                {t.label}
              </Chip>
            ))}
          </div>
        </div>

        {showBM && (
          <>
            <Field label="Consistency">
              <Select value={consistency} onChange={(e) => setConsistency(e.target.value as typeof consistency)}>
                <option value="">—</option>
                <option value="liquid">Liquid</option>
                <option value="soft">Soft</option>
                <option value="formed">Formed</option>
                <option value="hard">Hard</option>
              </Select>
            </Field>
            <Field label="Color" hint="Yellow is normal for breastfed babies; brown/tan for formula.">
              <Select value={color} onChange={(e) => setColor(e.target.value)}>
                <option value="">—</option>
                <option value="yellow">Yellow</option>
                <option value="brown">Brown</option>
                <option value="tan">Tan</option>
                <option value="green">Green</option>
                <option value="black">Black</option>
                <option value="red">Red — check with pediatrician</option>
                <option value="white">White — check with pediatrician</option>
              </Select>
            </Field>
          </>
        )}

        <Field label="When">
          <TextInput type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
        </Field>

        <Field label="Note (optional)">
          <TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>

        <PhotoAttachInput
          photos={photos}
          onChange={setPhotos}
          label="Photos (optional)"
          hint="For unusual color/consistency — a photo helps your pediatrician."
          compact
        />

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {err}
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" packColor={packColor} loading={busy} className="w-full">
          {isEditing ? 'Save correction' : 'Save diaper'}
        </Button>
      </form>
    </Screen>
  )
}
